import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  Avatar,
  Stack,
  Chip,
  Fade,
  Fab,
  alpha,
  useTheme,
  CircularProgress,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Psychology as AIIcon,
  BugReport as BugIcon,
  WaterDrop as WaterIcon,
  Grass as CropIcon,
  WbSunny as WeatherIcon,
} from '@mui/icons-material';
import { isDemoModeEnabled } from '../../services/featureFlags';
import { useAuth } from '../../context/AuthContext';
import { isAdminUser } from '../../utils/isAdminUser';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  type?: 'text' | 'suggestion';
}

const AIAgronomistChatbot: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: "Hello! I'm your AI Agronomist. How can I help you optimize your farm today?",
      sender: 'ai',
      timestamp: new Date(),
    },
  ]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: input,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    if (!isDemoModeEnabled()) {
      const responseMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: isAdmin
          ? 'Demo mode is disabled. Enable VITE_DEMO_MODE=true to use the demo chatbot, or connect a real AI backend.'
          : 'AI agronomist is not available yet. Please check back later.',
        sender: 'ai',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, responseMessage]);
      setIsTyping(false);
      return;
    }

    setTimeout(() => {
      let aiResponse = "I'm analyzing your data. Based on current soil moisture (35%) and the upcoming weather forecast (Rain in 48h), I recommend holding off on irrigation for your Maize crop.";
      
      if (input.toLowerCase().includes('pest') || input.toLowerCase().includes('bug')) {
        aiResponse = "I've detected patterns similar to Fall Armyworm in your region. Check the underside of your leaves for small eggs and consider applying organic neem oil early tomorrow morning.";
      } else if (input.toLowerCase().includes('price') || input.toLowerCase().includes('market')) {
        aiResponse = "Market trends show a 12% increase in Tomato prices in Nairobi next week. It might be the optimal time to harvest and ship.";
      }

      const responseMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: aiResponse,
        sender: 'ai',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, responseMessage]);
      setIsTyping(false);
    }, 1500);
  };

  const suggestions = [
    { text: 'Irrigation advice', icon: <WaterIcon fontSize="small" /> },
    { text: 'Pest identification', icon: <BugIcon fontSize="small" /> },
    { text: 'Crop health check', icon: <CropIcon fontSize="small" /> },
  ];

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="chat"
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
          boxShadow: theme.shadows[10],
          bgcolor: '#1a237e',
          '&:hover': { bgcolor: '#0d47a1' },
        }}
      >
        {isOpen ? <CloseIcon /> : <ChatIcon />}
      </Fab>

      {/* Chat Window */}
      <Fade in={isOpen}>
        <Paper
          elevation={12}
          sx={{
            position: 'fixed',
            bottom: 96,
            right: 24,
            width: { xs: 'calc(100% - 48px)', sm: 380 },
            height: 500,
            zIndex: 1000,
            borderRadius: '24px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: '1px solid',
            borderColor: alpha(theme.palette.primary.main, 0.1),
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2.5,
              bgcolor: '#1a237e',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Avatar sx={{ bgcolor: 'white' }}>
              <AIIcon sx={{ color: '#1a237e' }} />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">AI Agronomist</Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4caf50' }} />
                <Typography variant="caption" sx={{ opacity: 0.8 }}>Online & Ready to assist</Typography>
              </Stack>
            </Box>
          </Box>

          {/* Messages Area */}
          <Box
            sx={{
              flexGrow: 1,
              p: 2,
              overflowY: 'auto',
              bgcolor: '#f5f7fa',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {messages.map((msg) => (
              <Box
                key={msg.id}
                sx={{
                  alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: msg.sender === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                    bgcolor: msg.sender === 'user' ? '#1a237e' : 'white',
                    color: msg.sender === 'user' ? 'white' : 'text.primary',
                    boxShadow: msg.sender === 'user' ? 'none' : '0 2px 4px rgba(0,0,0,0.05)',
                  }}
                >
                  <Typography variant="body2">{msg.text}</Typography>
                </Paper>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5, textAlign: msg.sender === 'user' ? 'right' : 'left', px: 1 }}
                >
                  {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>
            ))}
            {isTyping && (
              <Box sx={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Paper elevation={0} sx={{ p: 1.5, borderRadius: '20px 20px 20px 4px', bgcolor: 'white' }}>
                  <Stack direction="row" spacing={0.5}>
                    <CircularProgress size={4} color="inherit" />
                    <CircularProgress size={4} color="inherit" />
                    <CircularProgress size={4} color="inherit" />
                  </Stack>
                </Paper>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area */}
          <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} mb={2} sx={{ overflowX: 'auto', pb: 0.5 }}>
              {suggestions.map((s) => (
                <Chip
                  key={s.text}
                  icon={s.icon}
                  label={s.text}
                  onClick={() => setInput(s.text)}
                  size="small"
                  variant="outlined"
                  sx={{ borderRadius: '8px', flexShrink: 0 }}
                />
              ))}
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Ask your agronomist..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    bgcolor: '#f8f9fa',
                  },
                }}
              />
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={!input.trim() || isTyping}
                sx={{
                  bgcolor: '#1a237e',
                  color: 'white',
                  '&:hover': { bgcolor: '#0d47a1' },
                  '&.Mui-disabled': { bgcolor: alpha('#1a237e', 0.3), color: 'white' },
                }}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        </Paper>
      </Fade>
    </>
  );
};

export default AIAgronomistChatbot;
